import React, { useEffect, useState } from "react";
import { Star } from "@phosphor-icons/react";
import { addWatchlist, removeWatchlist, getWatchlist } from "../lib/api";
import { useAuth } from "../auth/AuthProvider";
import { useNavigate } from "react-router-dom";

// Lightweight cache to avoid hammering the watchlist endpoint
let cachedIds = null;
let cachedAt = 0;
const subscribers = new Set();

const notifyAll = () => subscribers.forEach((fn) => fn());

const ensureLoaded = async () => {
  if (cachedIds && Date.now() - cachedAt < 30000) return cachedIds;
  try {
    const wl = await getWatchlist();
    cachedIds = new Set((wl.items || []).map((w) => String(w.id)));
  } catch {
    cachedIds = new Set();
  }
  cachedAt = Date.now();
  return cachedIds;
};

export default function StarButton({ market, size = 16, className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [starred, setStarred] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) {
      setStarred(false);
      return;
    }
    let mounted = true;
    ensureLoaded().then((ids) => {
      if (mounted) setStarred(ids.has(String(market.id)));
    });
    const onChange = () => {
      if (cachedIds) setStarred(cachedIds.has(String(market.id)));
    };
    subscribers.add(onChange);
    return () => {
      mounted = false;
      subscribers.delete(onChange);
    };
  }, [user, market.id]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      navigate("/about");
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      if (starred) {
        await removeWatchlist(market.id);
        cachedIds?.delete(String(market.id));
        setStarred(false);
      } else {
        await addWatchlist({
          id: String(market.id),
          question: market.question,
          image: market.image,
          yesPrice: market.yesPrice,
        });
        cachedIds?.add(String(market.id));
        setStarred(true);
      }
      notifyAll();
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={toggle}
      data-testid={`star-btn-${market.id}`}
      title={starred ? "Remove from watchlist" : "Add to watchlist"}
      className={`inline-flex items-center justify-center transition-colors ${
        starred ? "text-edge" : "text-zinc-500 hover:text-white"
      } ${className}`}
    >
      <Star size={size} weight={starred ? "fill" : "regular"} />
    </button>
  );
}
